import * as React from 'react';
import { LogoMark } from './LogoMark';

export const WORDMARK_BRAND = 'DENNIS VALCHEZ';
export const WORDMARK_TAGLINE = 'PSYCHIC · MEDIUM · TAROT READING';
// Sentence-case brand name for running text (copyright, bylines). Call sites
// should prefer the CMS value (text(content, 'brand.name')) and fall back here.
export const BRAND_NAME = 'Dennis Valchez';

// One canonical lockup style; call sites pick only the scale.
// nav 16px (navbar) · md 20px (splash) · lg 25px (footer/status/default).
type Size = 'nav' | 'md' | 'lg';

// Lockup lines are SVG text stretched to the crest width via textLength, so
// crest / brand / tagline are always the exact same width in any language.
// viewBox units: 100 = 1em of the tier font-size; 1060 = 10.6em crest width.
const LINE_W = 1060;

type LineProps = {
  text: string;
  className: string;
  fontSize: number;
  height: number;
  baseline: number;
};

function LockupLine({ text, className, fontSize, height, baseline }: LineProps) {
  return (
    <svg className={`lockup-line ${className}`} viewBox={`0 0 ${LINE_W} ${height}`}>
      <text x="0" y={baseline} fontSize={fontSize} textLength={LINE_W} lengthAdjust="spacing">
        {text}
      </text>
    </svg>
  );
}

type Props = {
  size?: Size;
  inverse?: boolean;
  showMark?: boolean;
  showBrand?: boolean;
  showTagline?: boolean;
  brandTitle?: string;
  taglineTitle?: string;
  gapSize?: 'sm' | 'md';
  align?: 'center' | 'left' | 'center-md-left';
};

const ALIGN_MAP: Record<NonNullable<Props['align']>, string> = {
  center: 'items-center text-center',
  left: 'items-start text-left',
  // Centred on mobile, left from md — for column layouts that stack on phones.
  'center-md-left': 'items-center text-center md:items-start md:text-left',
};

export function Wordmark({
  size = 'lg',
  inverse = false,
  showMark = true,
  showBrand = true,
  showTagline = true,
  brandTitle = WORDMARK_BRAND,
  taglineTitle = WORDMARK_TAGLINE,
  gapSize = 'md',
  align = 'center',
}: Props) {
  const withInverse = (className: string) =>
    className + (inverse ? ' ' + className + '-inverse' : '');
  const gapClass = gapSize === 'sm' ? 'gap-1' : 'gap-2';

  return (
    <div className={`wordmark wordmark--${size} flex flex-col ${gapClass} ${ALIGN_MAP[align]}`}>
      {showMark ? <LogoMark className={withInverse('t-logo')} /> : null}
      {showBrand && (
        <LockupLine
          text={brandTitle}
          className={withInverse('t-brand')}
          fontSize={100}
          height={112}
          baseline={86}
        />
      )}
      {showTagline ? (
        <LockupLine
          text={taglineTitle}
          className={withInverse('t-tagline')}
          fontSize={56}
          height={64}
          baseline={48}
        />
      ) : null}
    </div>
  );
}
